// CartPopup.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";

const CartPopup = ({ product, onClose, onComplete }) => {
  const [qty, setQty] = useState(1);

  // Shipping Details
  const [shipping, setShipping] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    landmark: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("COD");

  const handleShippingChange = (e) => {
    setShipping({ ...shipping, [e.target.name]: e.target.value });
  };

  const handlePlaceOrder = () => {
    for (const key in shipping) {
      if (!shipping[key]) return alert("Please fill all shipping details!");
    }

    onComplete(product, qty, paymentMethod, shipping);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-xl shadow-2xl p-6 w-[95%] max-h-[95vh] overflow-y-auto"
      >
        {/* HEADER */}
        <div className="flex justify-between items-center pb-4 border-b">
          <h2 className="text-xl font-semibold">Checkout</h2>
          <button className="text-2xl" onClick={onClose}>✖</button>
        </div>

        {/* MAIN CONTENT - 2 COLUMN LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">

          {/* LEFT SIDE - SHIPPING DETAILS */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Shipping Information</h3>

            <div className="grid grid-cols-2 gap-3">
              <input
                name="fullName"
                placeholder="Full Name"
                className="border p-2 rounded"
                value={shipping.fullName}
                onChange={handleShippingChange}
              />
              <input
                name="phone"
                placeholder="Phone Number"
                className="border p-2 rounded"
                value={shipping.phone}
                onChange={handleShippingChange}
              />
              <input
                name="email"
                placeholder="Email Address"
                className="border p-2 rounded col-span-2"
                value={shipping.email}
                onChange={handleShippingChange}
              />
              <input
                name="address"
                placeholder="Full Address"
                className="border p-2 rounded col-span-2"
                value={shipping.address}
                onChange={handleShippingChange}
              />
              <input
                name="city"
                placeholder="City"
                className="border p-2 rounded"
                value={shipping.city}
                onChange={handleShippingChange}
              />
              <input
                name="state"
                placeholder="State"
                className="border p-2 rounded"
                value={shipping.state}
                onChange={handleShippingChange}
              />
              <input
                name="pincode"
                placeholder="Pincode"
                className="border p-2 rounded"
                value={shipping.pincode}
                onChange={handleShippingChange}
              />
              <input
                name="landmark"
                placeholder="Nearby Landmark"
                className="border p-2 rounded"
                value={shipping.landmark}
                onChange={handleShippingChange}
              />
            </div>

            {/* PRODUCT SUMMARY */}
            <div className="mt-6 border rounded-lg p-4">
              <h4 className="font-semibold mb-2">Order Summary</h4>

              <div className="flex items-center gap-4">
                <img
                  src={product.thumbnail}
                  alt={product.title}
                  className="w-20 h-20 rounded object-cover border"
                />

                <div>
                  <p className="font-medium">{product.title}</p>

                  <div className="mt-1">
                    <p className="text-sm">Quantity:</p>
                    <select
                      value={qty}
                      onChange={(e) => setQty(Number(e.target.value))}
                      className="border px-2 py-1 rounded"
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>

                  <p className="font-semibold mt-1 text-green-700">
                    ₹{(qty * product.price).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - PAYMENT METHODS */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Payment Options</h3>

            <div className="border rounded-lg p-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="radio"
                  value="COD"
                  checked={paymentMethod === "COD"}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                Cash on Delivery
              </label>

              <label className="flex items-center gap-2 mb-3">
                <input
                  type="radio"
                  value="UPI"
                  checked={paymentMethod === "UPI"}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                UPI Payment
              </label>

              {paymentMethod === "UPI" && (
                <div className="mt-3">
                  <p className="text-sm mb-2 font-medium">Scan & Pay</p>
                  <img
                    src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay"
                    className="w-40 h-40 border rounded"
                  />
                </div>
              )}

              <label className="flex items-center gap-2 mb-3 mt-3">
                <input
                  type="radio"
                  value="Card"
                  checked={paymentMethod === "Card"}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                Credit / Debit Card
              </label>

              {paymentMethod === "Card" && (
                <div className="grid grid-cols-2 gap-3">
                  <input className="border p-2 rounded col-span-2" placeholder="Card Number" />
                  <input className="border p-2 rounded" placeholder="Expiry" />
                  <input className="border p-2 rounded" placeholder="CVV" />
                  <input className="border p-2 rounded col-span-2" placeholder="Card Holder Name" />
                </div>
              )}
            </div>

            {/* PLACE ORDER */}
            <button
              onClick={handlePlaceOrder}
              className="bg-indigo-600 text-white w-full py-3 mt-6 rounded-lg text-lg font-semibold"
            >
              Place Order
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CartPopup;
